import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useReactAriaButton } from '../../buttons/shared/useReactAriaButton.js';
import { useUnmountOnTransitionEnd } from '../../buttons/shared/useUnmountOnTransitionEnd.js';
import { Size } from '../../constants/size.js';
import { LoadingIcon } from '../../icons/LoadingIcon.js';
import { genericForwardRef } from '../../utils/genericForwardRef.js';
import { twMerge } from '../../utils/twMerge.js';
export const Tag = /* #__PURE__ */ genericForwardRef(function Tag({ size = Size.Default, variant = 'default', isLoading = false, isRounded = false, className, ...props }, forwardedRef) {
    const { elementType, isDisabled } = props;
    const { ref, buttonProps } = useReactAriaButton({
        ref: forwardedRef,
        props: isLoading || isDisabled ? { ...props, isDisabled: isLoading || isDisabled } : props,
    });
    const { shouldRender: shouldRenderLoadingIcon, unmountOnTransitionEnd } = useUnmountOnTransitionEnd(isLoading);
    const Component = elementType ?? 'div';
    const isInteractive = elementType === 'button' || elementType === 'a';
    return (_jsx(Component, { ...(isInteractive ? buttonProps : {}), ref: ref, "data-loading": isLoading || undefined, className: twMerge(
        // component name
        `ppl-Tag ppl-Tag--${variant}`, 
        // common styles
        'group relative inline-flex w-fit appearance-none items-center justify-center rounded align-baseline outline outline-1 -outline-offset-1 outline-transparent transition-[box-shadow,border-color,outline]', 
        // focus visible states
        'focus-visible:outline-accent-bold focus-visible:ring focus-visible:ring-accent/[.38] focus-visible:ring-offset-0', 
        // disabled states
        isDisabled && 'disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-40', isRounded && 'rounded-full', isInteractive && 'cursor-pointer select-none', 
        // sizes
        {
            compact: ['h-5 px-1 text-body-sm', isRounded && 'px-2', isLoading && 'pl-1'],
            default: [
                'h-6 px-2 text-body-md',
                isRounded && 'px-3',
                isLoading && 'pl-1',
                isLoading && isRounded && 'pl-2',
            ],
        }[size], 
        // variants
        {
            default: [
                'bg-default-bold text-default',
                isInteractive && [
                    // hover states
                    'hover:bg-default-bold-hover hover:text-default-hover hover:underline',
                    // pressed states
                    'pressed:bg-default-bold-hover pressed:text-default-hover pressed:underline',
                ],
            ],
            brand: [
                'bg-accent-subdued text-accent',
                isInteractive && [
                    // hover states
                    'hover:bg-accent-subdued-hover hover:text-accent-hover hover:underline',
                    // pressed states
                    'pressed:bg-accent-subdued-hover pressed:text-accent-hover pressed:underline',
                ],
            ],
            critical: [
                'bg-critical-subdued text-critical-bold',
                isInteractive && [
                    // hover states
                    'hover:bg-critical-subdued-hover hover:text-critical-hover hover:underline',
                    // pressed states
                    'pressed:bg-critical-subdued-hover pressed:text-critical-hover pressed:underline',
                ],
            ],
            warning: [
                'bg-warning-subdued text-warning-bold',
                isInteractive && [
                    // hover states
                    'hover:bg-warning-subdued-hover hover:text-warning-hover hover:underline',
                    // pressed states
                    'pressed:bg-warning-subdued-hover pressed:text-warning-hover pressed:underline',
                ],
            ],
            success: [
                'bg-success-subdued text-success-bold',
                isInteractive && [
                    // hover states
                    'hover:bg-success-subdued-hover hover:text-success-hover hover:underline',
                    // pressed states
                    'pressed:bg-success-subdued-hover pressed:text-success-hover pressed:underline',
                ],
            ],
            'accent-cornflower': [
                'bg-accent-cornflower-subdued text-accent-cornflower-bold',
                isInteractive && [
                    // hover states
                    'hover:bg-accent-cornflower-subdued-hover hover:text-accent-cornflower-hover hover:underline',
                    // pressed states
                    'pressed:bg-accent-cornflower-subdued-hover pressed:text-accent-cornflower-hover pressed:underline',
                ],
            ],
            'accent-orange': [
                'bg-accent-orange-subdued text-accent-orange-bold',
                isInteractive && [
                    // hover states
                    'hover:bg-accent-orange-subdued-hover hover:text-accent-orange-hover hover:underline',
                    // pressed states
                    'pressed:bg-accent-orange-subdued-hover pressed:text-accent-orange-hover pressed:underline',
                ],
            ],
            'accent-lime': [
                'bg-accent-lime-subdued text-accent-lime-bold',
                isInteractive && [
                    // hover states
                    'hover:bg-accent-lime-subdued-hover hover:text-accent-lime-hover hover:underline',
                    // pressed states
                    'pressed:bg-accent-lime-subdued-hover pressed:text-accent-lime-hover pressed:underline',
                ],
            ],
        }[variant], 
        // user provided class names
        className), children: _jsxs("span", { className: twMerge('inline-flex max-w-full items-center justify-center gap-1 truncate whitespace-nowrap transition-all', isLoading && 'ml-5'), children: [shouldRenderLoadingIcon ? (_jsx(LoadingIcon, { className: twMerge('absolute left-1 animate-spin opacity-0 transition-opacity group-data-[loading=true]:opacity-100 motion-reduce:animate-none', isRounded &&
                        {
                            default: 'group-data-[loading=true]:left-2',
                            compact: 'group-data-[loading=true]:left-1',
                        }[size]), "aria-label": "Loading", onTransitionEnd: unmountOnTransitionEnd })) : null, props.children] }) }));
});
//# sourceMappingURL=Tag.js.map